// The tiny client that runs INSIDE a surface iframe. It is the surface's only
// channel out, and it speaks ONLY to window.parent (the host). It learns its
// instanceId from the host `init` handshake, applies Slate's theme variables to
// its own document, and buffers any outbound message until init completes. It
// validates that every inbound frame's source is the parent, so a nested or
// sibling frame cannot inject. See the plan's security model.

import {
  SURFACE_CHANNEL,
  isSurfaceFrame,
  type SurfaceFrame,
  type SurfaceToHostFrame,
  type ThemeSnapshot,
} from './protocol';

export interface SurfaceClientDisposable {
  dispose(): void;
}

export interface SlateSurfaceClient {
  postMessage(message: unknown): void;
  onMessage(cb: (message: unknown) => void): SurfaceClientDisposable;
  theme(): ThemeSnapshot;
}

export function createSurfaceClient(scope?: Window): SlateSurfaceClient {
  const win = scope ?? (globalThis as unknown as { window: Window }).window;
  const parent = win.parent;
  let instanceId: string | null = null;
  let snapshot: ThemeSnapshot = {};
  const outbound: unknown[] = [];
  const subscribers = new Set<(message: unknown) => void>();

  const postUp = (frame: SurfaceToHostFrame): void => {
    // The iframe has an opaque origin, so the host cannot be targeted by a
    // specific origin; '*' is forced. The host trusts by source identity.
    parent.postMessage(frame, '*');
  };

  const flush = (): void => {
    if (instanceId === null) return;
    for (const message of outbound.splice(0)) {
      postUp({ channel: SURFACE_CHANNEL, t: 'message', instanceId, message });
    }
  };

  const applyTheme = (theme: ThemeSnapshot): void => {
    snapshot = theme;
    const root = win.document?.documentElement;
    if (!root) return;
    for (const [name, value] of Object.entries(theme)) {
      root.style.setProperty(name, value);
    }
  };

  win.addEventListener('message', (e: MessageEvent): void => {
    // SECURITY: only the host (our parent) may message us.
    if (e.source !== parent) return;
    const data: unknown = e.data;
    if (!isSurfaceFrame(data)) return;
    const frame = data as SurfaceFrame;
    if (frame.t === 'init') {
      instanceId = frame.instanceId;
      applyTheme(frame.theme);
      flush();
      return;
    }
    if (frame.t === 'message') {
      for (const cb of [...subscribers]) cb(frame.message);
    }
  });

  // Announce readiness; the host replies with `init`.
  postUp({ channel: SURFACE_CHANNEL, t: 'ready' });

  return {
    postMessage(message) {
      if (instanceId === null) {
        outbound.push(message);
        return;
      }
      postUp({ channel: SURFACE_CHANNEL, t: 'message', instanceId, message });
    },
    onMessage(cb) {
      subscribers.add(cb);
      return { dispose: () => void subscribers.delete(cb) };
    },
    theme() {
      return snapshot;
    },
  };
}

/** Default singleton over the real window, for a plugin's bundled surface. */
export const slateSurface: SlateSurfaceClient =
  typeof window !== 'undefined'
    ? createSurfaceClient(window)
    : // A non-DOM context (e.g. SSR/import in tests) gets an inert stub.
      {
        postMessage: () => undefined,
        onMessage: () => ({ dispose: () => undefined }),
        theme: () => ({}),
      };
